import React, { useId } from 'react';

/**
 * Giftedict Logo Component
 *
 * The iconic "G" shaped gift box logo - a clever combination of
 * the letter G and a gift box with pink bow, yellow band, and sparkles.
 */

// SVG Logo Icon - G-shaped Gift Box (matches original logo)
export function LogoIcon({ size = 32, animated = true, className = '' }) {
  // Generate unique IDs for this instance to avoid conflicts
  const id = useId();

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={`${animated ? 'logo-animated' : ''} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Soft shadow for depth */}
        <filter id={`shadow-${id}`} x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#333" floodOpacity="0.15" />
        </filter>
      </defs>

      {/* G-shaped gift box outline - the iconic shape */}
      {/* Main box body - forms the "G" shape */}
      <path
        d="M12 20
           L52 20
           L52 26
           L18 26
           L18 52
           L52 52
           L52 38
           L32 38
           L32 44
           L46 44
           L46 46
           L32 46
           L32 38
           L52 38
           L52 58
           L12 58
           L12 20 Z"
        fill="white"
        stroke="#4A4A4A"
        strokeWidth="3"
        strokeLinejoin="round"
        filter={`url(#shadow-${id})`}
      />

      {/* Yellow horizontal band */}
      <rect
        x="12"
        y="20"
        width="40"
        height="8"
        fill="#F5C519"
        stroke="#4A4A4A"
        strokeWidth="2.5"
        rx="1"
      />

      {/* Inner horizontal line of the G */}
      <rect
        x="30"
        y="38"
        width="22"
        height="6"
        fill="white"
        stroke="#4A4A4A"
        strokeWidth="2"
      />

      {/* Pink bow - Left loop */}
      <ellipse
        cx="24"
        cy="14"
        rx="8"
        ry="6"
        fill="#FFB6C1"
        stroke="#4A4A4A"
        strokeWidth="2"
        transform="rotate(-20 24 14)"
      />

      {/* Pink bow - Right loop */}
      <ellipse
        cx="40"
        cy="14"
        rx="8"
        ry="6"
        fill="#FFB6C1"
        stroke="#4A4A4A"
        strokeWidth="2"
        transform="rotate(20 40 14)"
      />

      {/* Pink bow - Center knot */}
      <ellipse
        cx="32"
        cy="17"
        rx="4"
        ry="5"
        fill="#FFB6C1"
        stroke="#4A4A4A"
        strokeWidth="2"
      />

      {/* Sparkles */}
      {/* Yellow 4-point star - top right */}
      <g className={animated ? 'sparkle-1' : ''}>
        <path
          d="M54 8 L55 10 L57 11 L55 12 L54 14 L53 12 L51 11 L53 10 Z"
          fill="#F5C519"
        />
      </g>

      {/* Pink 4-point star - left */}
      <g className={animated ? 'sparkle-2' : ''}>
        <path
          d="M6 28 L7 29.5 L8.5 30 L7 30.5 L6 32 L5 30.5 L3.5 30 L5 29.5 Z"
          fill="#FFB6C1"
        />
      </g>

      {/* Cyan dot - top left */}
      <circle className={animated ? 'sparkle-3' : ''} cx="8" cy="16" r="2" fill="#5DD3D3" />

      {/* Yellow dot - bottom right */}
      <circle className={animated ? 'sparkle-1' : ''} cx="58" cy="50" r="1.5" fill="#F5C519" />

      {/* Pink dot - bottom */}
      <circle className={animated ? 'sparkle-2' : ''} cx="24" cy="60" r="1.5" fill="#FFB6C1" />

      {/* Small cyan dot */}
      <circle className={animated ? 'sparkle-3' : ''} cx="56" cy="28" r="1" fill="#5DD3D3" />

      <style>{`
        .logo-animated path,
        .logo-animated rect,
        .logo-animated ellipse,
        .logo-animated circle {
          transition: all 0.3s ease;
        }

        .logo-animated:hover {
          transform: scale(1.05);
        }

        .sparkle-1 {
          animation: twinkle 2s ease-in-out infinite;
        }

        .sparkle-2 {
          animation: twinkle 2s ease-in-out infinite 0.5s;
        }

        .sparkle-3 {
          animation: twinkle 2s ease-in-out infinite 1s;
        }

        @keyframes twinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
      `}</style>
    </svg>
  );
}

// Full Logo with Text
export function Logo({
  size = 'md',
  showText = true,
  animated = true,
  variant = 'default', // 'default' | 'white' | 'dark'
  className = '',
}) {
  const sizes = {
    sm: { icon: 24, text: 'text-lg' },
    md: { icon: 32, text: 'text-xl' },
    lg: { icon: 48, text: 'text-2xl' },
    xl: { icon: 64, text: 'text-3xl' },
  };

  const textColors = {
    default: 'bg-gradient-to-r from-coral-600 to-coral-500 bg-clip-text text-transparent',
    white: 'text-white',
    dark: 'text-slate-900',
  };

  const { icon, text } = sizes[size] || sizes.md;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <LogoIcon size={icon} animated={animated} />
      {showText && (
        <span className={`font-bold ${text} ${textColors[variant]}`}>
          Giftedict
        </span>
      )}
    </div>
  );
}

// Logo Badge - Compact version for app header (G-shaped icon)
export function LogoBadge({ size = 32, className = '' }) {
  return (
    <div
      className={`
        rounded-xl overflow-hidden
        bg-white
        flex items-center justify-center
        shadow-md
        ${className}
      `}
      style={{ width: size, height: size }}
    >
      <LogoIcon size={size * 0.85} animated={false} />
    </div>
  );
}

// Premium Logo - With glow effect for special occasions
export function PremiumLogo({ className = '' }) {
  return (
    <div className={`relative ${className}`}>
      {/* Glow background */}
      <div className="absolute inset-0 bg-gradient-to-r from-coral-500/30 to-gold-500/30 blur-2xl rounded-full scale-150" />

      {/* Main logo */}
      <div className="relative">
        <Logo size="xl" animated />
      </div>
    </div>
  );
}

// Animated Loading Logo
export function LoadingLogo({ size = 48, className = '' }) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="relative">
        <LogoIcon size={size} animated />
        <div
          className="absolute inset-0 animate-spin-slow"
          style={{
            background: 'conic-gradient(from 0deg, transparent 0%, rgba(244,63,94,0.3) 50%, transparent 100%)',
            borderRadius: '50%',
          }}
        />
      </div>
    </div>
  );
}

// Full screen splash logo
export function SplashLogo({ onComplete, duration = 2000 }) {
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex items-center justify-center animate-fade-out" style={{ animationDelay: `${duration - 500}ms` }}>
      <div className="text-center animate-bounce-in">
        <LogoIcon size={80} animated />
        <h1 className="mt-4 text-3xl font-bold text-slate-800">
          Giftedict
        </h1>
        <p className="mt-2 text-slate-500">Every gift tells a story</p>
      </div>

      <style>{`
        @keyframes fade-out {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-fade-out {
          animation: fade-out 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default Logo;
